/**
 * Generative-UI renderer for the `plot_chart` tool, wired into the styled Thread via
 * its `components.Tools` by-name registry (see SphereAIThread). This is the supported,
 * non-deprecated replacement for `useAssistantToolUI`: the Thread renders this component
 * for `plot_chart` tool-call parts and surfaces it standalone (outside the collapsed
 * tool group).
 *
 * The renderer is purely presentational — it draws the chart from the tool-call *input*
 * (the model's spec). The agent loop itself is closed in `SphereAIChatSection.onToolCall`,
 * which acks the call so the model can continue.
 */
import { Highcharts } from "$root/third-party-integrations/highcharts-with-modules";
import type { ToolCallMessagePartComponent } from "@assistant-ui/react";
import type { ThreadComponents } from "@mdotm/shadcn/assistant-ui";
import HighchartsReact from "highcharts-react-official";
import { useMemo } from "react";
import { buildChartOptions } from "./buildChartOptions";
import { PLOT_CHART_TOOL_NAME, parseChartSpec } from "./chartSpec";

const SphereAIChartRender: ToolCallMessagePartComponent = ({ args, status }) => {
	const parsed = useMemo(() => parseChartSpec(args), [args]);
	const options = useMemo(() => (parsed.ok ? buildChartOptions(parsed.spec) : null), [parsed]);

	// While the model is still streaming the tool arguments the spec is incomplete and
	// won't validate yet — show a placeholder instead of an error.
	if (!parsed.ok) {
		if (status?.type === "running") {
			return (
				<div className="text-muted-foreground my-2 flex h-80 w-full items-center justify-center rounded-lg border border-border text-sm">
					Preparing chart…
				</div>
			);
		}
		// The chart it would have been is wide, but this is a sentence: it belongs in the thread's
		// reading lane, like the prose around it, rather than stretched across the whole column.
		return (
			<div className="text-muted-foreground my-2 mx-auto w-full max-w-(--thread-max-width) rounded-lg border border-border p-3 text-sm">
				Couldn't render the chart: {parsed.error}
			</div>
		);
	}

	return (
		<div className="my-2 w-full rounded-lg border border-border p-2">
			<HighchartsReact highcharts={Highcharts} options={options} containerProps={{ style: { height: "20rem" } }} />
		</div>
	);
};

/**
 * `Tools` registry entry registering the `plot_chart` generative-UI renderer. Spread into
 * the Thread's by-name registry (see SphereAIThread). A stable module constant so the Thread
 * can memoize its part grouping across renders.
 */
export const sphereAIChartTools = {
	[PLOT_CHART_TOOL_NAME]: SphereAIChartRender,
} satisfies NonNullable<ThreadComponents["Tools"]>;
